import { createHybridTheme } from '../themes/hybrid';
import type { ThemeName } from '../themes/tokens';

/**
 * Synchronous theme cache.
 *
 * `chrome.storage.local` is asynchronous, so the theme cannot be read before
 * the browser paints the popup for the first time. That caused a visible flash
 * on every open: the popup painted with the default light theme, then swapped
 * to the user's real theme once the async read resolved.
 *
 * To avoid that we mirror the theme into `localStorage` (which IS synchronous
 * and shared across every extension page of the same origin). The cached value
 * is read and applied to the DOM before React mounts, so the first frame is
 * already correct.
 */
const THEME_CACHE_KEY = 'tpl-theme';

/** Read the cached theme synchronously. Returns null if unavailable/unset. */
export function getCachedTheme(): string | null {
  try {
    return localStorage.getItem(THEME_CACHE_KEY);
  } catch {
    // localStorage can be unavailable (e.g. disabled). Fall back to defaults.
    return null;
  }
}

/** Mirror the theme into the synchronous cache for the next open. */
export function setCachedTheme(theme: string): void {
  try {
    localStorage.setItem(THEME_CACHE_KEY, theme);
  } catch {
    // Ignore write failures — the app still works, it just may flash once.
  }
}

/**
 * Apply a theme to the document: sets the `data-theme` attribute, toggles the
 * Tailwind `dark` class, and injects the theme's CSS custom properties so that
 * `var(--app-background)` and friends resolve on the very first paint.
 *
 * Safe to call before React mounts and idempotent (replaces the existing
 * `#theme-vars` style element in place rather than stacking new ones).
 */
export function applyThemeToDom(theme: string): void {
  const root = document.documentElement;

  root.setAttribute('data-theme', theme);
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  const hybridTheme = createHybridTheme(theme as ThemeName);
  const cssVars = Object.entries(hybridTheme.colors).flatMap(([section, colors]) =>
    Object.entries(colors as Record<string, string>).map(([key, value]) => {
      const cssKey = `${section}-${key}`.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
      return `--${cssKey}: ${value};`;
    })
  );

  const style = document.createElement('style');
  style.setAttribute('id', 'theme-vars');
  style.textContent = `:root { ${cssVars.join(' ')} }`;

  const existingStyle = document.getElementById('theme-vars');
  if (existingStyle) {
    existingStyle.replaceWith(style);
  } else {
    document.head.appendChild(style);
  }
}
